import { AddAPhoto } from "@mui/icons-material";
import { Box, Button, List, Tooltip, Typography } from "@mui/material";
import { SatelliteIcon } from "common/icons";
import { useTranslation } from "common/locales";
import {
  DiaryEntry,
  Field,
  ManualDMInputResponse,
  ManualDMInputResponseItem,
  useDiaryEntriesForField,
  useReadingsForField,
} from "model";
import { useMemo, useState } from "react";
import DiaryEntryListItem from "./DiaryEntryListItem";
import FieldEventListItem from "./FieldEventListItem";
import NewDiaryEntryDialog from "./NewDiaryEntryDialog";

const BELLFLOWER_COLOR = "#D3C9E7";

export default function FieldEventList({ field }: { field: Field }) {
  const { t } = useTranslation();
  const [diaryEntryOpen, setDiaryEntryOpen] = useState(false);

  const handleDiaryEntryOpen = () => {
    setDiaryEntryOpen(true);
  };

  const handleDiaryEntryClose = () => {
    setDiaryEntryOpen(false);
    // React Query will automatically refetch when the mutation invalidates the cache
  };

  // Use React Query hooks that will trigger the data fetch
  const { data: allReadings = [] } = useReadingsForField(field.guid);
  const { data: diaryEntriesForField = [] } = useDiaryEntriesForField(
    field.farm,
    field.guid
  );

  // Filter readings to only show non-rejected events for this field
  const manualDMForField = useMemo(() => {
    return allReadings.filter(
      (reading) =>
        reading.manualDryMatterInput.field === field.guid &&
        reading.manualDryMatterInput.verificationStatus !== "Rejected"
    );
  }, [allReadings, field.guid]);

  // see https://stackoverflow.com/questions/51528780/typescript-check-typeof-against-custom-type
  const isDiaryEntry = (x: unknown): x is DiaryEntry =>
    typeof x === "object" && x !== null && "entryDate" in x;
  const isManualDMInputResponse = (x: unknown): x is ManualDMInputResponse =>
    typeof x === "object" && x !== null && "manualDryMatterInput" in x;

  const getDate = (event: ManualDMInputResponse | DiaryEntry): Date => {
    if (isDiaryEntry(event)) {
      return new Date(event.entryDate);
    }
    if (isManualDMInputResponse(event)) {
      return new Date(getLatestTime(event.manualDryMatterInput));
    }
    return new Date(1);
  };

  let allEvents: Array<ManualDMInputResponse | DiaryEntry> = [];

  allEvents = allEvents.concat(manualDMForField).concat(diaryEntriesForField);

  // This will now sort on every render and we did not find out how to fix it, see https://github.com/robofarmio/web-app-frontend/pull/775#discussion_r1502445622
  // we might go back to 2021 methods like "onMount" etc to be able to compare if we find this is too slow
  const sortedEventList = allEvents.sort(
    (
      a: ManualDMInputResponse | DiaryEntry,
      b: ManualDMInputResponse | DiaryEntry
    ) => {
      const dateA: Date = getDate(a);
      const dateB: Date = getDate(b);
      return dateB.valueOf() - dateA.valueOf();
    }
  );

  return (
    <Box sx={{ mb: 2 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
          }}
        >
          {t("fieldevents.list.title")}
        </Typography>
        <Button
          startIcon={<AddAPhoto sx={{ fontSize: "16px !important" }} />}
          size="small"
          onClick={handleDiaryEntryOpen}
          sx={{
            color: "primary.main",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            padding: "4px 8px",
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "rgba(56, 142, 60, 0.08)",
            },
          }}
        >
          {t("fieldview.addImage")}
        </Button>
      </Box>

      {/* Events List Container */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          border: `1px solid ${BELLFLOWER_COLOR}`,
          overflow: "hidden",
        }}
      >
        <List sx={{ py: 0 }}>
          {sortedEventList.map((dm: ManualDMInputResponse | DiaryEntry) => {
            if (isManualDMInputResponse(dm)) {
              return <FieldEventListItem key={dm.guid} dm={dm} field={field} />;
            } else {
              return <DiaryEntryListItem key={dm.guid} diaryEntry={dm} />;
            }
          })}
        </List>
      </Box>

      <NewDiaryEntryDialog
        isOpen={diaryEntryOpen}
        close={handleDiaryEntryClose}
        fieldGuid={field.guid}
        farmId={field.farm}
      />
    </Box>
  );
}

export function AutoDetectedIcon() {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("fieldevent.autodetected.tooltip")}>
      <div>
        <SatelliteIcon />
      </div>
    </Tooltip>
  );
}

function getLatestTime(response: ManualDMInputResponseItem) {
  const { event: e } = response;
  if (e.dateTimeOut) {
    // covers move out and grazing/partial grazing events
    return Date.parse(e.dateTimeOut);
  }

  if (e.dateTimeIn) {
    // covers move in event
    return Date.parse(e.dateTimeIn);
  }

  if (e.dateTimeCut) {
    // silage event
    return Date.parse(e.dateTimeCut);
  }

  return Date.parse(response.dateTimeMeasurementStart); // reading
}
